import { gql } from '@apollo/client'

// Cross-entity Inquiry queries (FLIGHT-688 / FLIGHT-694).
export const INQUIRY_SEARCH = gql`
  query InquirySearch($term: String!) {
    inquirySearch(term: $term) {
      type
      id
      title
      subtitle
      badge
    }
  }
`

export const INQUIRY_ENTITY = gql`
  query InquiryEntity($type: String!, $id: String!) {
    inquiryEntity(type: $type, id: $id) {
      type
      id
      title
      subtitle
      badge
      alert
      facts {
        label
        value
        alert
      }
      groups {
        label
        cards {
          type
          id
          title
          subtitle
          status
          alert
        }
      }
    }
  }
`

// Independent transactions panel (FLIGHT-702 / FLIGHT-703 / FLIGHT-705): loads on
// its own request so it can search / filter / page without re-fetching the whole
// entity. `filter.mode` selects 'db' (keyword) or 'rag' (semantic); `appliedMode`
// reports what the server could actually serve. Filtering, ordering, paging and the
// in/out/net summary all happen in SQL, so the totals describe the whole filtered
// history rather than the rows on screen.
export const INQUIRY_TRANSACTIONS = gql`
  query InquiryTransactions($type: String!, $id: String!, $filter: InquiryTransactionFilter) {
    inquiryTransactions(type: $type, id: $id, filter: $filter) {
      items {
        id
        source
        date
        kind
        direction
        amount
        description
        status
        tags {
          type
          id
          label
        }
      }
      total
      totalIn
      totalOut
      net
      page
      pageSize
      pageCount
      appliedMode
      semanticCoverage
    }
  }
`

// Fee metrics and answers over the fee ledger (FLIGHT-736 / FLIGHT-737).
// The registry is permission-scoped server side, so this returns only what the
// caller may ask about.
export const FEE_METRICS = gql`
  query FeeMetrics {
    feeMetrics {
      name
      version
      label
      definition
      dimensions
    }
  }
`

// The answer envelope. Period, filters, definitions, result and detail always
// travel together - a number without its evidence is not an acceptable answer.
// Kept as a fragment because a picked metric and a typed question return the same
// envelope; a question that answered with less evidence than a click would be a
// second, weaker contract.
const FEE_ANSWER_FIELDS = gql`
  fragment FeeAnswerFields on FeeAnswer {
    metric
    metricVersion
    label
    definition
    period {
      from
      to
      basis
    }
    groupBy
    filters {
      labelKey
      valueKeys
      value
    }
    total
    entryCount
    breakdown {
      labelKey
      label
      amount
      entries
    }
    detail {
      id
      kind
      direction
      entryType
      amount
      assessedAt
      settledAt
      sourceType
      sourceId
      fundId
      charityId
      note
    }
    detailTotalCount
  }
`

// A typed question (FLIGHT-739). The planner selects a registered metric and
// fills its parameters; it never composes a query. `interpretation` is what it
// understood, shown to the reader rather than assumed.
export const FEE_ASK = gql`
  ${FEE_ANSWER_FIELDS}
  query FeeAsk($question: String!, $detailTake: Int, $from: Date, $to: Date) {
    feeAsk(question: $question, detailTake: $detailTake, from: $from, to: $to) {
      question
      confidence
      refusal {
        reason
        unsupported
        choices
        canAnswer
        supportedPeriodKeys
      }
      interpretation {
        metricLabel
        periodSource
        dimensionSource
      }
      answer {
        ...FeeAnswerFields
      }
    }
  }
`

export const FEE_ANSWER = gql`
  ${FEE_ANSWER_FIELDS}
  query FeeAnswer(
    $metric: String!
    $from: Date!
    $to: Date!
    $basis: FeePeriodBasis
    $groupBy: FeeDimension
    $charityId: String
    $detailSkip: Int
    $detailTake: Int
  ) {
    feeAnswer(
      metric: $metric
      from: $from
      to: $to
      basis: $basis
      groupBy: $groupBy
      charityId: $charityId
      detailSkip: $detailSkip
      detailTake: $detailTake
    ) {
      ...FeeAnswerFields
    }
  }
`
